import { z } from 'zod';
import { CountryCodeSchema } from './geo';
import { VehicleClassSchema } from './riders';

/* ------------------------------------------------------------------ */
/* Control tower (W15)                                                 */
/*                                                                     */
/* Capsico OS › Control tower: four live panels (order funnel, kitchen */
/* delays, fleet grid, outside signals) per town / zone, an auto-acting */
/* worker (busy kitchens, rain mode, festival mode) whose actions Ops   */
/* can undo, kill switches + emergency modes (persisted + audited),     */
/* the festival calendar, road blocks / bandh notes, weather, and the   */
/* Town Fleet Captain's alert inbox (scoped to their towns).            */
/* ------------------------------------------------------------------ */

const Reason = z.string().trim().min(3, 'Say why (at least 3 characters)').max(300);
const Id = z.uuid();

/** Polling fallback for the Control tower when the live socket is down. */
export const TOWER_POLL_SECONDS = 15;
/** Worker cadence (the auto-acting rules run every tick). */
export const TOWER_TICK_SECONDS = 5;
/** Longest a restaurant pause may run (design: ≤ 4 h). */
export const TOWER_MAX_RESTAURANT_PAUSE_MINUTES = 240;

/* ---------------- kill switches + emergency modes ---------------- */

export const KILL_SWITCH_KINDS = [
  'zone_off',
  'restaurant_pause',
  'cod_off',
  'rain_mode',
  'festival_mode',
  'bandh',
] as const;
export const KillSwitchKindSchema = z.enum(KILL_SWITCH_KINDS);
export type KillSwitchKind = z.infer<typeof KillSwitchKindSchema>;

export const KILL_SWITCH_LABELS: Record<KillSwitchKind, string> = {
  zone_off: 'Zone off',
  restaurant_pause: 'Restaurant pause',
  cod_off: 'Cash on delivery off',
  rain_mode: 'Rain mode',
  festival_mode: 'Festival mode',
  bandh: 'Bandh',
};

export const SWITCH_SCOPES = ['country', 'town', 'zone', 'outlet'] as const;
export const SwitchScopeSchema = z.enum(SWITCH_SCOPES);
export type SwitchScope = z.infer<typeof SwitchScopeSchema>;

/** Which scopes each switch may target. */
export const KILL_SWITCH_SCOPES: Record<KillSwitchKind, readonly SwitchScope[]> = {
  zone_off: ['zone'],
  restaurant_pause: ['outlet'],
  cod_off: ['zone', 'town', 'country'],
  rain_mode: ['town'],
  festival_mode: ['town'],
  bandh: ['zone', 'town'],
};

/** Default expiry when the person flipping the switch does not pick one (minutes). */
export const KILL_SWITCH_DEFAULT_MINUTES: Record<KillSwitchKind, number> = {
  zone_off: 120,
  restaurant_pause: 60,
  cod_off: 240,
  rain_mode: 60,
  festival_mode: 360,
  bandh: 720,
};

/** Longest expiry a switch may have (minutes). */
export const KILL_SWITCH_MAX_MINUTES: Record<KillSwitchKind, number> = {
  zone_off: 1440,
  restaurant_pause: TOWER_MAX_RESTAURANT_PAUSE_MINUTES,
  cod_off: 4320,
  rain_mode: 720,
  festival_mode: 1440,
  bandh: 2880,
};

/** What each switch does, shown next to the switch. */
export const KILL_SWITCH_EFFECTS: Record<KillSwitchKind, string> = {
  zone_off:
    'Restaurants in the zone disappear from the app and checkout refuses new orders. Live orders continue.',
  restaurant_pause:
    'The outlet goes offline with your reason and comes back on its own when the pause ends.',
  cod_off: 'Checkout hides cash on delivery for new orders here.',
  rain_mode:
    'Smaller delivery radius, rain fee and longer delivery times. Heavy rain also stops offers to walkers and cyclists.',
  festival_mode: 'Festival delivery radius, festival fee and longer delivery times.',
  bandh: 'No new orders in the area (live orders finish). No surge fees.',
};

export const SWITCH_STATUSES = ['active', 'ended', 'expired'] as const;
export const SwitchStatusSchema = z.enum(SWITCH_STATUSES);
export type SwitchStatus = z.infer<typeof SwitchStatusSchema>;

export const SWITCH_SOURCES = ['ops', 'tower'] as const;
export type SwitchSource = (typeof SWITCH_SOURCES)[number];

export const KillSwitchSchema = z.object({
  id: Id,
  kind: KillSwitchKindSchema,
  scope: SwitchScopeSchema,
  country: CountryCodeSchema,
  townId: Id.nullable(),
  townName: z.string().nullable(),
  zoneId: Id.nullable(),
  zoneName: z.string().nullable(),
  outletId: Id.nullable(),
  outletName: z.string().nullable(),
  /** Heavy rain (rain mode only): walkers and cyclists get no offers. */
  heavy: z.boolean(),
  reason: z.string(),
  source: z.enum(SWITCH_SOURCES),
  status: SwitchStatusSchema,
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  endedBy: z.string().nullable(),
  endNote: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  version: z.number().int(),
});
export type KillSwitch = z.infer<typeof KillSwitchSchema>;

export const KillSwitchListQuerySchema = z.object({
  townId: Id.optional(),
  status: z.enum(['active', 'all']).default('active'),
  kind: KillSwitchKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type KillSwitchListQuery = z.infer<typeof KillSwitchListQuerySchema>;

export const KillSwitchListSchema = z.object({ rows: z.array(KillSwitchSchema) });
export type KillSwitchList = z.infer<typeof KillSwitchListSchema>;

export const KillSwitchCreateInputSchema = z
  .object({
    kind: KillSwitchKindSchema,
    country: CountryCodeSchema.optional(),
    townId: Id.optional(),
    zoneId: Id.optional(),
    outletId: Id.optional(),
    reason: Reason,
    /** Omitted = the switch's default; the switch never runs longer than its maximum. */
    expiresInMinutes: z.number().int().min(5).max(4320).optional(),
    heavy: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    const target = value.outletId
      ? 'outlet'
      : value.zoneId
        ? 'zone'
        : value.townId
          ? 'town'
          : value.country
            ? 'country'
            : null;
    if (!target) {
      ctx.addIssue({ code: 'custom', message: 'Pick where the switch applies', path: ['townId'] });
      return;
    }
    if (!KILL_SWITCH_SCOPES[value.kind].includes(target)) {
      ctx.addIssue({
        code: 'custom',
        message: `${KILL_SWITCH_LABELS[value.kind]} cannot be set for a whole ${target}`,
        path: ['kind'],
      });
    }
    if (
      value.expiresInMinutes !== undefined &&
      value.expiresInMinutes > KILL_SWITCH_MAX_MINUTES[value.kind]
    ) {
      ctx.addIssue({
        code: 'custom',
        message: `${KILL_SWITCH_LABELS[value.kind]} can run for at most ${KILL_SWITCH_MAX_MINUTES[value.kind]} minutes`,
        path: ['expiresInMinutes'],
      });
    }
    if (value.heavy && value.kind !== 'rain_mode') {
      ctx.addIssue({
        code: 'custom',
        message: 'Heavy rain only applies to rain mode',
        path: ['heavy'],
      });
    }
  });
export type KillSwitchCreateInput = z.infer<typeof KillSwitchCreateInputSchema>;

export const KillSwitchEndInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  note: z.string().trim().max(300).optional(),
});
export type KillSwitchEndInput = z.infer<typeof KillSwitchEndInputSchema>;

/* ---------------- automatic actions (undoable) ---------------- */

export const TOWER_ACTION_KINDS = [
  'kitchen_busy',
  'kitchen_busy_revert',
  'rain_mode',
  'festival_mode',
  'switch_on',
  'switch_off',
] as const;
export const TowerActionKindSchema = z.enum(TOWER_ACTION_KINDS);
export type TowerActionKind = z.infer<typeof TowerActionKindSchema>;

export const TOWER_ACTION_LABELS: Record<TowerActionKind, string> = {
  kitchen_busy: 'Kitchen marked busy',
  kitchen_busy_revert: 'Kitchen back to normal',
  rain_mode: 'Rain mode on',
  festival_mode: 'Festival mode on',
  switch_on: 'Switch on',
  switch_off: 'Switch off',
};

export const TOWER_ACTION_STATUSES = ['applied', 'undone', 'reverted'] as const;
export const TowerActionStatusSchema = z.enum(TOWER_ACTION_STATUSES);
export type TowerActionStatus = z.infer<typeof TowerActionStatusSchema>;

export const TowerActionSchema = z.object({
  id: Id,
  kind: TowerActionKindSchema,
  /** "tower" for the worker, otherwise the person (actor key). */
  actor: z.string(),
  actorName: z.string().nullable(),
  automatic: z.boolean(),
  townId: Id.nullable(),
  townName: z.string().nullable(),
  outletId: Id.nullable(),
  outletName: z.string().nullable(),
  switchId: Id.nullable(),
  reason: z.string(),
  before: z.record(z.string(), z.unknown()),
  after: z.record(z.string(), z.unknown()),
  status: TowerActionStatusSchema,
  at: z.string(),
  undoUntil: z.string().nullable(),
  canUndo: z.boolean(),
  undoneAt: z.string().nullable(),
  undoneBy: z.string().nullable(),
});
export type TowerAction = z.infer<typeof TowerActionSchema>;

export const TowerActionListQuerySchema = z.object({
  townId: Id.optional(),
  automatic: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type TowerActionListQuery = z.infer<typeof TowerActionListQuerySchema>;

export const TowerActionListSchema = z.object({ rows: z.array(TowerActionSchema) });
export type TowerActionList = z.infer<typeof TowerActionListSchema>;

export const TowerUndoInputSchema = z.object({
  note: z.string().trim().max(300).optional(),
});
export type TowerUndoInput = z.infer<typeof TowerUndoInputSchema>;

/* ---------------- alerts ---------------- */

export const ALERT_SEVERITIES = ['info', 'warning', 'critical'] as const;
export const AlertSeveritySchema = z.enum(ALERT_SEVERITIES);
export type AlertSeverity = z.infer<typeof AlertSeveritySchema>;

export const TOWER_ALERT_KINDS = [
  'unaccepted',
  'no_rider',
  'late_surge',
  'heavy_rain',
  'kitchen_busy',
  'gps_suspect',
  'weather_offline',
  /** W25: job:audit-verify found a broken tamper-evident audit chain (critical, no town). */
  'audit_chain_broken',
] as const;
export const TowerAlertKindSchema = z.enum(TOWER_ALERT_KINDS);
export type TowerAlertKind = z.infer<typeof TowerAlertKindSchema>;

export const TOWER_ALERT_LABELS: Record<TowerAlertKind, string> = {
  unaccepted: 'Order not accepted',
  no_rider: 'No rider found',
  late_surge: 'Many late orders',
  heavy_rain: 'Heavy rain',
  kitchen_busy: 'Busy kitchen',
  gps_suspect: 'Possible fake GPS',
  weather_offline: 'Weather feed down',
  audit_chain_broken: 'Audit chain broken',
};

export const ALERT_STATUSES = ['open', 'acknowledged', 'resolved'] as const;
export const AlertStatusSchema = z.enum(ALERT_STATUSES);
export type AlertStatus = z.infer<typeof AlertStatusSchema>;

export const TowerAlertSchema = z.object({
  id: Id,
  kind: TowerAlertKindSchema,
  severity: AlertSeveritySchema,
  status: AlertStatusSchema,
  townId: Id.nullable(),
  townName: z.string().nullable(),
  zoneId: Id.nullable(),
  orderId: Id.nullable(),
  orderNo: z.number().int().nullable(),
  riderId: Id.nullable(),
  outletId: Id.nullable(),
  title: z.string(),
  detail: z.string().nullable(),
  createdAt: z.string(),
  acknowledgedBy: z.string().nullable(),
  acknowledgedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolveNote: z.string().nullable(),
});
export type TowerAlert = z.infer<typeof TowerAlertSchema>;

export const TowerAlertListQuerySchema = z.object({
  townId: Id.optional(),
  status: z.enum(['open', 'acknowledged', 'resolved', 'unresolved', 'all']).default('unresolved'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type TowerAlertListQuery = z.infer<typeof TowerAlertListQuerySchema>;

export const TowerAlertListSchema = z.object({
  rows: z.array(TowerAlertSchema),
  counts: z.object({ open: z.number().int(), acknowledged: z.number().int() }),
});
export type TowerAlertList = z.infer<typeof TowerAlertListSchema>;

export const TowerAlertResolveInputSchema = z.object({ note: Reason });
export type TowerAlertResolveInput = z.infer<typeof TowerAlertResolveInputSchema>;

/* ---------------- festival calendar ---------------- */

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

export const FestivalDaySchema = z.object({
  id: Id,
  date: IsoDate,
  name: z.string(),
  country: CountryCodeSchema,
  townId: Id.nullable(),
  townName: z.string().nullable(),
  extraMinutes: z.number().int(),
  createdBy: z.string(),
  createdAt: z.string(),
});
export type FestivalDay = z.infer<typeof FestivalDaySchema>;

export const FestivalListQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export type FestivalListQuery = z.infer<typeof FestivalListQuerySchema>;

export const FestivalListSchema = z.object({ rows: z.array(FestivalDaySchema) });
export type FestivalList = z.infer<typeof FestivalListSchema>;

export const FestivalCreateInputSchema = z.object({
  date: IsoDate,
  name: z.string().trim().min(2, 'Name the festival').max(80),
  country: CountryCodeSchema,
  townId: Id.optional(),
  extraMinutes: z.number().int().min(0).max(60).default(10),
});
export type FestivalCreateInput = z.infer<typeof FestivalCreateInputSchema>;

/* ---------------- road blocks / bandh notes ---------------- */

export const ROAD_NOTE_KINDS = ['road_block', 'bandh', 'other'] as const;
export const RoadNoteKindSchema = z.enum(ROAD_NOTE_KINDS);
export type RoadNoteKind = z.infer<typeof RoadNoteKindSchema>;

export const ROAD_NOTE_LABELS: Record<RoadNoteKind, string> = {
  road_block: 'Road block',
  bandh: 'Bandh',
  other: 'Other',
};

const LngLat = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
/** A GeoJSON polygon (one outer ring, closed: first point = last point). */
export const RoadPolygonSchema = z.object({
  type: z.literal('Polygon'),
  coordinates: z
    .array(z.array(LngLat).min(4).max(500))
    .min(1)
    .max(1)
    .refine((rings) => {
      const ring = rings[0];
      if (!ring) return false;
      const first = ring[0];
      const last = ring[ring.length - 1];
      return !!first && !!last && first[0] === last[0] && first[1] === last[1];
    }, 'The shape must be closed'),
});
export type RoadPolygon = z.infer<typeof RoadPolygonSchema>;

export const RoadNoteSchema = z.object({
  id: Id,
  townId: Id,
  townName: z.string(),
  kind: RoadNoteKindSchema,
  title: z.string(),
  note: z.string().nullable(),
  polygon: RoadPolygonSchema.nullable(),
  startsAt: z.string(),
  endsAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  active: z.boolean(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
});
export type RoadNote = z.infer<typeof RoadNoteSchema>;

export const RoadNoteListQuerySchema = z.object({
  townId: Id.optional(),
  active: z
    .enum(['true', 'false'])
    .transform((value) => value === 'true')
    .default(true),
});
export type RoadNoteListQuery = z.infer<typeof RoadNoteListQuerySchema>;

export const RoadNoteListSchema = z.object({ rows: z.array(RoadNoteSchema) });
export type RoadNoteList = z.infer<typeof RoadNoteListSchema>;

export const RoadNoteCreateInputSchema = z.object({
  townId: Id,
  kind: RoadNoteKindSchema,
  title: z.string().trim().min(3, 'Give it a short title').max(80),
  note: z.string().trim().max(500).optional(),
  polygon: RoadPolygonSchema.optional(),
  expiresInMinutes: z.number().int().min(15).max(10080).optional(),
});
export type RoadNoteCreateInput = z.infer<typeof RoadNoteCreateInputSchema>;

/* ---------------- weather ---------------- */

export const WEATHER_SIGNALS = ['clear', 'rain', 'heavy_rain', 'unknown'] as const;
export const WeatherSignalSchema = z.enum(WEATHER_SIGNALS);
export type WeatherSignal = z.infer<typeof WeatherSignalSchema>;

export const WEATHER_SIGNAL_LABELS: Record<WeatherSignal, string> = {
  clear: 'Dry',
  rain: 'Rain',
  heavy_rain: 'Heavy rain',
  unknown: 'Unknown',
};

export const TownWeatherSchema = z.object({
  townId: Id,
  townName: z.string(),
  country: CountryCodeSchema,
  signal: WeatherSignalSchema,
  precipMmPerHour: z.number().nullable(),
  weatherCode: z.number().int().nullable(),
  /** When the reading was taken (null = never read). */
  at: z.string().nullable(),
  stale: z.boolean(),
});
export type TownWeather = z.infer<typeof TownWeatherSchema>;

export const TowerWeatherQuerySchema = z.object({ townId: Id.optional() });
export type TowerWeatherQuery = z.infer<typeof TowerWeatherQuerySchema>;

export const TowerWeatherListSchema = z.object({ rows: z.array(TownWeatherSchema) });
export type TowerWeatherList = z.infer<typeof TowerWeatherListSchema>;

/* ---------------- overview (the four panels + number cards) ---------------- */

export const TowerOverviewQuerySchema = z.object({
  townId: Id.optional(),
  zoneId: Id.optional(),
});
export type TowerOverviewQuery = z.infer<typeof TowerOverviewQuerySchema>;

export const TOWER_FUNNEL_STAGES = [
  'placed',
  'accepted',
  'preparing',
  'ready',
  'assigned',
  'picked_up',
  'delivered',
  'cancelled',
] as const;
export type TowerFunnelStage = (typeof TOWER_FUNNEL_STAGES)[number];

export const TOWER_FUNNEL_LABELS: Record<TowerFunnelStage, string> = {
  placed: 'Placed',
  accepted: 'Accepted',
  preparing: 'Preparing',
  ready: 'Ready',
  assigned: 'Rider assigned',
  picked_up: 'Picked up',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const OrderRef = z.object({
  orderId: Id,
  orderNo: z.number().int(),
  outletId: Id,
  outletName: z.string(),
  townId: Id,
});

export const TowerTownSchema = z.object({
  id: Id,
  name: z.string(),
  country: CountryCodeSchema,
});
export type TowerTown = z.infer<typeof TowerTownSchema>;

export const TowerOverviewSchema = z.object({
  at: z.string(),
  /** Towns this person may see (Fleet Captains: only their own). */
  towns: z.array(TowerTownSchema),
  townId: Id.nullable(),
  zoneId: Id.nullable(),
  /** Money on the page is in this country's currency (null when towns of both countries show). */
  country: CountryCodeSchema.nullable(),
  /** The last worker heartbeat (null = the worker has not run). */
  workerBeatAt: z.string().nullable(),
  canAct: z.boolean(),
  canManage: z.boolean(),
  cards: z.object({
    liveOrders: z.number().int(),
    /** Share of today's delivered orders that were late (null = none delivered yet). */
    latePercent: z.number().nullable(),
    noRiderLong: z.number().int(),
    noRiderMinutes: z.number().int(),
    cancelledToday: z.number().int(),
    ridersOnline: z.number().int(),
  }),
  funnel: z.object({
    counts: z.record(z.enum(TOWER_FUNNEL_STAGES), z.number().int()),
    /** Placed longer than tower.unaccepted_alert_minutes and still not accepted (rows flash red). */
    unaccepted: z.array(OrderRef.extend({ placedAt: z.string(), minutes: z.number() })),
    unacceptedMinutes: z.number().int(),
  }),
  kitchens: z.object({
    busy: z.array(
      z.object({
        outletId: Id,
        outletName: z.string(),
        townId: Id,
        liveOrders: z.number().int(),
        rushMinutes: z.number().int(),
        towerApplied: z.boolean(),
      }),
    ),
    readyNoRider: z.array(OrderRef.extend({ readyAt: z.string(), minutes: z.number() })),
    prepLate: z.array(OrderRef.extend({ readyBy: z.string(), lateMinutes: z.number() })),
    busyThreshold: z.number().int(),
  }),
  fleet: z.object({
    byClass: z.array(
      z.object({
        vehicleClass: VehicleClassSchema,
        online: z.number().int(),
        idle: z.number().int(),
        onTrip: z.number().int(),
        paused: z.boolean(),
      }),
    ),
    earnings: z.object({
      avgMinor: z.number().int().nullable(),
      lowest: z.array(
        z.object({ riderId: Id, name: z.string(), earningsTodayMinor: z.number().int() }),
      ),
    }),
    cashLimit: z.array(
      z.object({
        riderId: Id,
        name: z.string(),
        cashHeldMinor: z.number().int(),
        limitMinor: z.number().int(),
      }),
    ),
    gpsSuspect: z.array(
      z.object({
        riderId: Id,
        name: z.string(),
        tripId: Id,
        orderNo: z.number().int().nullable(),
      }),
    ),
    ladder: z.array(
      OrderRef.extend({
        step: z.number().int(),
        minutesSearching: z.number(),
        paused: z.boolean(),
      }),
    ),
  }),
  signals: z.object({
    weather: z.array(TownWeatherSchema),
    switches: z.array(KillSwitchSchema),
    festivalsToday: z.array(FestivalDaySchema),
    notes: z.array(RoadNoteSchema),
  }),
  alerts: z.object({ open: z.number().int(), critical: z.number().int() }),
});
export type TowerOverview = z.infer<typeof TowerOverviewSchema>;

/* ---------------- Town Fleet Captains ---------------- */

export const CaptainSchema = z.object({
  identityId: Id,
  name: z.string().nullable(),
  phoneMasked: z.string(),
  status: z.string(),
  towns: z.array(TowerTownSchema),
});
export type Captain = z.infer<typeof CaptainSchema>;

export const CaptainListSchema = z.object({
  rows: z.array(CaptainSchema),
  towns: z.array(TowerTownSchema),
});
export type CaptainList = z.infer<typeof CaptainListSchema>;

export const CaptainTownsInputSchema = z.object({
  townIds: z.array(Id).max(50),
});
export type CaptainTownsInput = z.infer<typeof CaptainTownsInputSchema>;

/* ---------------- live events ---------------- */

/** ops:town:<id> room event names for the Control tower (the W14 socket). */
export const TOWER_LIVE_EVENTS = {
  alert: 'tower.alert',
  update: 'tower.update',
} as const;

export const TOWER_UPDATE_KINDS = ['switch', 'action', 'weather', 'alert', 'note'] as const;
export const TowerUpdateEventSchema = z.object({
  townId: Id,
  kind: z.enum(TOWER_UPDATE_KINDS),
  at: z.string(),
});
export type TowerUpdateEvent = z.infer<typeof TowerUpdateEventSchema>;
